import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, ExternalLink, Mail, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/PageShell';
import SEO from '../components/SEO';
import { GIVEBUTTER_URL } from '../mock';
import { useI18n } from '../contexts/I18nContext';

const GIVEBUTTER_WIDGET_ID = 'jN8xrg';

export default function DonatePage() {
  const { t } = useI18n();
  const tiers = [
    { amount: '$25', label: t('donate.tier1.label'), desc: t('donate.tier1.desc') },
    { amount: '$75', label: t('donate.tier2.label'), desc: t('donate.tier2.desc') },
    { amount: '$250', label: t('donate.tier3.label'), desc: t('donate.tier3.desc') },
    { amount: '$1,000', label: t('donate.tier4.label'), desc: t('donate.tier4.desc') },
  ];

  // Re-process the Givebutter widget if the user navigates here client-side
  // (the script normally only auto-scans on first load).
  useEffect(() => {
    const w = window;
    if (w.Givebutter && typeof w.Givebutter.refresh === 'function') {
      try { w.Givebutter.refresh(); } catch { /* noop */ }
    }
  }, []);

  return (
    <PageShell
      eyebrow={t('donate.eyebrow')}
      title={<>{t('donate.title1')} <span className="italic text-[#E5614C]">{t('donate.title2')}</span></>}
      subtitle={t('donate.subtitle')}
      accent="coral"
    >
      <SEO
        title="Donate & Support Our Work"
        description="Your gift to GEMS @ The Arts keeps our programs free and accessible for youth, families, and artists in Framingham, MA. Make a one-time donation, become a monthly sustainer, or sponsor a season of joy."
        path="/donate"
      />

      {/* Inject the Givebutter widget script only when this page mounts */}
      <Helmet>
        <script src="https://widgets.givebutter.com/latest.umd.cjs?acct=EtoYNgiT72t0xEJZ&p=other" async></script>
      </Helmet>

      {/* Givebutter embed */}
      <section className="pb-10" data-testid="donate-givebutter-section">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="relative rounded-[28px] bg-white border border-[#2A1B1A]/8 overflow-hidden shadow-[0_30px_60px_-30px_rgba(42,27,26,0.25)]">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E5614C]/8 via-transparent to-[#5DCAB5]/10 pointer-events-none" />
            <div className="relative p-7 lg:p-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">

              {/* Left: framing & alt CTAs */}
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E5614C]/10 text-[#E5614C] text-[12px] font-semibold uppercase tracking-[0.18em]">
                  <Heart className="h-3.5 w-3.5" /> {t('donate.gb.giveSecurely')}
                </div>
                <h2 className="font-serif-display text-[30px] lg:text-[40px] font-bold text-[#2A1B1A] mt-4 leading-[1.05]">
                  {t('donate.gb.heading1')} <span className="italic text-[#E5614C]">{t('donate.gb.heading2')}</span>
                </h2>
                <p className="text-[15px] text-[#2A1B1A]/70 mt-4 leading-relaxed">
                  {t('donate.gb.intro')}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild variant="outline" className="rounded-full border-2 border-[#E5614C] text-[#E5614C] hover:bg-[#E5614C] hover:text-white bg-transparent font-semibold h-11 px-5 btn-press">
                    <a href={GIVEBUTTER_URL} target="_blank" rel="noopener noreferrer">
                      {t('donate.gb.openExt')} <ExternalLink className="h-4 w-4 ml-1.5" />
                    </a>
                  </Button>
                  <Button asChild variant="ghost" className="rounded-full text-[#2A1B1A] hover:bg-[#2A1B1A]/5 font-semibold h-11 px-5">
                    <a href="mailto:info@gemsatthearts.org?subject=Donation Inquiry">
                      <Mail className="h-4 w-4 mr-1.5" /> {t('donate.gb.otherWays')}
                    </a>
                  </Button>
                </div>
                <p className="mt-6 text-[12px] text-[#2A1B1A]/50 leading-relaxed">
                  {t('donate.gb.taxNote')}
                </p>
              </div>

              {/* Right: the actual Givebutter widget */}
              <div className="lg:col-span-7" data-testid="givebutter-widget-mount">
                <div className="rounded-2xl bg-[#FAF3EE] border border-[#2A1B1A]/8 p-3 sm:p-4 min-h-[480px]">
                  <givebutter-widget id={GIVEBUTTER_WIDGET_ID}></givebutter-widget>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Giving tiers */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
              <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('donate.tiers.eyebrow')}
            </div>
            <h2 className="font-serif-display text-[34px] sm:text-[42px] lg:text-[52px] font-bold text-[#2A1B1A] mt-4 leading-[1.05]">
              {t('donate.tiers.title1')} <span className="italic text-[#E5614C]">{t('donate.tiers.title2')}</span>{t('donate.tiers.title3')}
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((tier, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-[#2A1B1A]/8 hover:border-[#E5614C]/30 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-25px_rgba(229,97,76,0.35)] transition-[transform,box-shadow,border-color] duration-300"
              >
                <div className="font-serif-display text-[36px] font-bold text-[#E5614C]">{tier.amount}</div>
                <div className="mt-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#5DCAB5]">{tier.label}</div>
                <p className="mt-3 text-[14px] text-[#2A1B1A]/70 leading-relaxed">{tier.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate sponsorship - full width with teal style */}
      <section className="py-16 pb-24">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="rounded-[24px] bg-[#5DCAB5] text-white p-8 lg:p-12 grid md:grid-cols-[auto,1fr,auto] gap-6 md:gap-10 items-center">
            <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
              <Users className="h-7 w-7" />
            </div>
            <div className="text-left">
              <h3 className="font-serif-display text-[28px] lg:text-[36px] font-bold">{t('donate.sponsor.title')}</h3>
              <p className="mt-2 text-white/90 leading-relaxed text-[15px] lg:text-[16px] max-w-3xl">{t('donate.sponsor.desc')}</p>
            </div>
            <Button asChild className="rounded-full bg-white text-[#5DCAB5] hover:bg-[#FAF3EE] font-semibold h-11 px-7 btn-press shrink-0">
              <a href="mailto:info@gemsatthearts.org?subject=Sponsorship Inquiry">{t('donate.sponsor.cta')}</a>
            </Button>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
